/**
 * This module defines the constants and types for:
 *  - NEAR Intents asset addresses
 *  - Expected address formats for withdrawals
 *  - Mapping to Defuse chain IDs
 *
 * Every asset deposited on the NEAR Intents Chain has a unique address.
 * For example, depositing Base USDC and Solana USDC results in two distinct addresses.
 */

import { getContrastColor } from '@infinex/ui-utils/color';

import type { ChainDecoration } from './chains/configTypes';
import type { ChainKey } from './chains/derivedTypes';

/* ==================================== */
/*      NEAR Intents Asset Addresses    */
/* ==================================== */

// Unique addresses for assets on the NEAR Intents Chain
// The PoA bridge mints `nbtc.bridge.near` for native BTC deposits. It
// previously minted `btc.omft.near` ("Bitcoin (Legacy)" in the upstream token
// registry); existing balances of it remain visible and withdrawable.
export const NEAR_INTENTS_BITCOIN_ADDRESS = 'nep141:nbtc.bridge.near';
export const NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS = 'nep141:btc.omft.near';
export const NEAR_INTENTS_DOGECOIN_ADDRESS = 'nep141:doge.omft.near';
export const NEAR_INTENTS_XRP_ADDRESS = 'nep141:xrp.omft.near';
export const NEAR_INTENTS_ZEC_ADDRESS = 'nep141:zec.omft.near';
export const NEAR_INTENTS_ADA_ADDRESS = 'nep141:cardano.omft.near';
export const NEAR_INTENTS_LTC_ADDRESS = 'nep141:ltc.omft.near';

// Derived types for each asset address
export type NearIntentsBitcoinAddress = typeof NEAR_INTENTS_BITCOIN_ADDRESS;
export type NearIntentsBitcoinLegacyAddress =
  typeof NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS;
export type NearIntentsDogecoinAddress = typeof NEAR_INTENTS_DOGECOIN_ADDRESS;
export type NearIntentsXrpAddress = typeof NEAR_INTENTS_XRP_ADDRESS;
export type NearIntentsZecAddress = typeof NEAR_INTENTS_ZEC_ADDRESS;
export type NearIntentsAdaAddress = typeof NEAR_INTENTS_ADA_ADDRESS;
export type NearIntentsLtcAddress = typeof NEAR_INTENTS_LTC_ADDRESS;

export const NEAR_INTENTS_ASSETS: `nep141:${string}`[] = [
  NEAR_INTENTS_BITCOIN_ADDRESS,
  NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS,
  NEAR_INTENTS_DOGECOIN_ADDRESS,
  NEAR_INTENTS_XRP_ADDRESS,
  NEAR_INTENTS_ZEC_ADDRESS,
  NEAR_INTENTS_ADA_ADDRESS,
  NEAR_INTENTS_LTC_ADDRESS,
];

export type NearIntentsAddress = (typeof NEAR_INTENTS_ASSETS)[number];

export function isNearIntentsAsset(asset: string): asset is NearIntentsAddress {
  return NEAR_INTENTS_ASSETS.includes(asset as NearIntentsAddress);
}

// Map of defuse asset ID to chain address ID
export const DEFUSE_ASSET_ID_TO_CHAIN_ADDRESS_ID: Record<
  NearIntentsAddress,
  string
> = {
  [NEAR_INTENTS_BITCOIN_ADDRESS]: 'nearIntents_bitcoin',
  // Both BTC tokens share the `bitcoin` market identity — they represent the
  // same underlying asset, so price/market lookups resolve to the same group.
  [NEAR_INTENTS_BITCOIN_LEGACY_ADDRESS]: 'nearIntents_bitcoin',
  [NEAR_INTENTS_DOGECOIN_ADDRESS]: 'nearIntents_dogecoin',
  [NEAR_INTENTS_XRP_ADDRESS]: 'nearIntents_xrp',
  [NEAR_INTENTS_ZEC_ADDRESS]: 'nearIntents_zec',
  [NEAR_INTENTS_ADA_ADDRESS]: 'nearIntents_ada',
  [NEAR_INTENTS_LTC_ADDRESS]: 'nearIntents_ltc',
};

/* ============================== */
/*         Defuse Chain IDs       */
/* ============================== */

// Defuse chain IDs corresponding to each asset
export const BITCOIN_DEFUSE_CHAIN_ID = 'btc:mainnet';
export const DOGECOIN_DEFUSE_CHAIN_ID = 'doge:mainnet';
export const XRP_DEFUSE_CHAIN_ID = 'xrp:mainnet';
export const ZEC_DEFUSE_CHAIN_ID = 'zec:mainnet';
export const CARDANO_DEFUSE_CHAIN_ID = 'cardano:mainnet';
export const LTC_DEFUSE_CHAIN_ID = 'ltc:mainnet';
export const SOLANA_DEFUSE_CHAIN_ID = 'sol:mainnet';
export const ETHEREUM_DEFUSE_CHAIN_ID = 'eth:1';
export const BASE_DEFUSE_CHAIN_ID = 'eth:8453';
export const ARBITRUM_DEFUSE_CHAIN_ID = 'eth:42161';
export const BERACHAIN_DEFUSE_CHAIN_ID = 'eth:80094';
export const NEAR_DEFUSE_CHAIN_ID = 'near:mainnet';

// WHEN RE-ENABLING, MAKE SURE TO UNCOMMENT IN
// - apps/web-app/src/lib/chains/components.tsx
// - workers/main/src/swidge/utils/quote.ts
// export const POLYGON_DEFUSE_CHAIN_ID = 'eth:137';
// export const BSC_DEFUSE_CHAIN_ID = 'eth:56';

// List of all Defuse chain IDs in use in the wallet -- ONLY NEAR INTENTS ASSETS
export const NEAR_INTENTS_DEFUSE_CHAIN_IDS = [
  BITCOIN_DEFUSE_CHAIN_ID,
  DOGECOIN_DEFUSE_CHAIN_ID,
  XRP_DEFUSE_CHAIN_ID,
  ZEC_DEFUSE_CHAIN_ID,
  CARDANO_DEFUSE_CHAIN_ID,
  LTC_DEFUSE_CHAIN_ID,
] as const;

export type DefuseChainId = (typeof NEAR_INTENTS_DEFUSE_CHAIN_IDS)[number];

export function isDefuseChainId(chainId: string): chainId is DefuseChainId {
  return NEAR_INTENTS_DEFUSE_CHAIN_IDS.includes(chainId as DefuseChainId);
}
// Union type for Defuse chain IDs -- ALL ASSETS, ONLY USED IN SWIDGE
// TODO: @MIN Bandaid type waiting refactor to combine this and DefuseChainId, add all to NEAR_INTENTS_DEFUSE_CHAIN_IDS
// TODO: @BEN then filter out in anything wallet-related through feature flags
export const ExtendedDefuseChainIds = [
  ...NEAR_INTENTS_DEFUSE_CHAIN_IDS,
  SOLANA_DEFUSE_CHAIN_ID,
  ETHEREUM_DEFUSE_CHAIN_ID,
  BASE_DEFUSE_CHAIN_ID,
  ARBITRUM_DEFUSE_CHAIN_ID,
  BERACHAIN_DEFUSE_CHAIN_ID,
  NEAR_DEFUSE_CHAIN_ID,
  // BSC_DEFUSE_CHAIN_ID,
  // POLYGON_DEFUSE_CHAIN_ID,
] as const;

export type ExtendedDefuseChainId = (typeof ExtendedDefuseChainIds)[number];

// Map each supported chain key to its corresponding defuse chain ID
export const DEFUSE_CHAIN_ID_BY_CHAIN_KEY: Record<
  Exclude<NearIntentsSupportedChainKey, 'nearIntents'>,
  ExtendedDefuseChainId
> = {
  arbitrum: ARBITRUM_DEFUSE_CHAIN_ID,
  base: BASE_DEFUSE_CHAIN_ID,
  berachain: BERACHAIN_DEFUSE_CHAIN_ID,
  ethereum: ETHEREUM_DEFUSE_CHAIN_ID,
  near: NEAR_DEFUSE_CHAIN_ID,
  solana: SOLANA_DEFUSE_CHAIN_ID,
  // polygon: POLYGON_DEFUSE_CHAIN_ID,
  // bsc: BSC_DEFUSE_CHAIN_ID,
};

export const NearIntentsSupportedChainKeys = [
  'arbitrum',
  'base',
  'berachain',
  'ethereum',
  'near',
  'solana',
  'nearIntents',
  // 'polygon',
  // 'bsc',
] as const satisfies ChainKey[];
export type NearIntentsSupportedChainKey =
  (typeof NearIntentsSupportedChainKeys)[number];

export function isNearIntentsSupportedChain(chainKey: ChainKey) {
  return NearIntentsSupportedChainKeys.includes(
    chainKey as NearIntentsSupportedChainKey
  );
}
export const CHAIN_KEY_TO_NEAR_INTENTS_CHAIN_ABBREVIATION: Record<
  Exclude<NearIntentsSupportedChainKey, 'nearIntents'>,
  string
> = {
  solana: 'sol',
  arbitrum: 'arb',
  ethereum: 'eth',
  base: 'base',
  berachain: 'bera',
  near: 'near',
  // polygon: 'pol',
  // bsc: 'bsc',
};

/** Sub runtimes of nearIntents assets */
export const NEAR_INTENTS_RUNTIMES = [
  'bitcoin',
  'xrp',
  'dogecoin',
  'zcash',
  'cardano',
  'litecoin',
] as const;

export type NearIntentsRuntime = (typeof NEAR_INTENTS_RUNTIMES)[number];

type NearIntentsChainMetadata = {
  name: string;
  displayName: string;
  nearIntentsRuntime: NearIntentsRuntime;
  assetId: string; // we can make this an [] later if we support multiple assets per runtime/chain, but for now this disgusting monstrosity suffices
  /**
   * Asset ids of tokens on this chain that the PoA bridge no longer mints but
   * that users may still hold (e.g. the pre-nBTC-migration BTC token). They
   * resolve to the same runtime/chain as `assetId`.
   */
  legacyAssetIds?: string[];
  blockExplorer: string;
  /**
   * [Dicey] Order priority within NearIntents. Not directly comparable to ChainConfig.orderPriority.
   */
  orderPriority: number;
  decoration: ChainDecoration;
  depositProcessingTime: string;
};

export function nearIntentsRuntimeToDefuseChainId(
  runtime: NearIntentsRuntime | null
): DefuseChainId | 'nearIntents' {
  if (!runtime) return 'nearIntents';
  const found = (
    Object.entries(NEAR_INTENTS_CHAIN_METADATA) as [
      DefuseChainId,
      NearIntentsChainMetadata,
    ][]
  ).find(([_, info]) => info.nearIntentsRuntime === runtime);
  if (!found) return 'nearIntents';
  return found[0];
}

// Constants for chainConstants.ts in web-shared
export const NEAR_INTENTS_CHAIN_METADATA: Record<
  DefuseChainId,
  NearIntentsChainMetadata
> = {
  [BITCOIN_DEFUSE_CHAIN_ID]: {
    name: 'Bitcoin',
    displayName: 'Bitcoin',
    nearIntentsRuntime: 'bitcoin',
    assetId: 'bitcoin',
    legacyAssetIds: ['bitcoin_legacy'],
    blockExplorer: 'https://mempool.space',
    orderPriority: 1,
    decoration: {
      color: '#F7931A',
      contrastColor: getContrastColor('#F7931A'),
    },
    depositProcessingTime: '20 minutes',
  },
  [DOGECOIN_DEFUSE_CHAIN_ID]: {
    name: 'Dogecoin',
    displayName: 'Dogecoin',
    nearIntentsRuntime: 'dogecoin',
    assetId: 'dogecoin',
    blockExplorer: 'https://dogechain.info',
    orderPriority: 3,
    decoration: {
      color: '#E1B303',
      contrastColor: getContrastColor('#E1B303'),
    },
    depositProcessingTime: '20 minutes',
  },
  [XRP_DEFUSE_CHAIN_ID]: {
    name: 'XRP Ledger',
    displayName: 'XRP Ledger',
    nearIntentsRuntime: 'xrp',
    assetId: 'xrp',
    blockExplorer: 'https://xrpscan.com',
    orderPriority: 4,
    decoration: {
      color: '#FACB40',
      contrastColor: getContrastColor('#FACB40'),
    },
    depositProcessingTime: '2 minutes',
  },
  [ZEC_DEFUSE_CHAIN_ID]: {
    name: 'Zcash',
    displayName: 'Zcash',
    nearIntentsRuntime: 'zcash',
    assetId: 'zec',
    blockExplorer: 'https://3xpl.com/zcash',
    orderPriority: 5,
    decoration: {
      color: '#F4B728',
      contrastColor: getContrastColor('#F4B728'),
    },
    depositProcessingTime: '2 minutes',
  },
  [CARDANO_DEFUSE_CHAIN_ID]: {
    name: 'Cardano',
    displayName: 'Cardano',
    nearIntentsRuntime: 'cardano',
    assetId: 'ada',
    blockExplorer: 'https://cardanoscan.io',
    orderPriority: 6,
    decoration: {
      color: '#0033ad',
      contrastColor: getContrastColor('#0033ad'),
    },
    depositProcessingTime: '2 minutes',
  },
  [LTC_DEFUSE_CHAIN_ID]: {
    name: 'Litecoin',
    displayName: 'Litecoin',
    nearIntentsRuntime: 'litecoin',
    assetId: 'ltc',
    blockExplorer: 'https://litecoinspace.org/',
    orderPriority: 2,
    decoration: {
      color: '#345D9D',
      contrastColor: getContrastColor('#345D9D'),
    },
    depositProcessingTime: '20 minutes',
  },
};

export const NEAR_INTENTS_ASSET_IDS = Object.values(
  NEAR_INTENTS_CHAIN_METADATA
).map((metadata) => metadata.assetId);

function getNearIntentsChainMetadataFromAssetId(assetId: string): {
  defuseChainId: DefuseChainId;
  nearIntentsChainMetadata: NearIntentsChainMetadata;
} | null {
  const found = (
    Object.entries(NEAR_INTENTS_CHAIN_METADATA) as [
      DefuseChainId,
      NearIntentsChainMetadata,
    ][]
  ).find(
    ([_, metadata]) =>
      metadata.assetId === assetId || metadata.legacyAssetIds?.includes(assetId)
  );
  if (!found) return null;
  return {
    defuseChainId: found[0],
    nearIntentsChainMetadata: found[1],
  };
}

export function getNearIntentsRuntimeFromAssetId(
  assetId?: string
): NearIntentsRuntime | null {
  if (!assetId) return null;
  const metadata = getNearIntentsChainMetadataFromAssetId(assetId);
  if (!metadata) return null;
  return metadata.nearIntentsChainMetadata.nearIntentsRuntime;
}

export function getNearIntentsDefuseChainIdFromAssetId(
  assetId?: string
): DefuseChainId | null {
  if (!assetId) return null;
  const metadata = getNearIntentsChainMetadataFromAssetId(assetId);
  if (!metadata) return null;
  return metadata.defuseChainId;
}

// Used for Validating BTC/DOGE addresses
export const networks = {
  bitcoin: {
    mainnet: {
      /**
       * The prefix for Bitcoin public key hashes.
       */
      pubKeyHash: 0x00,
      /**
       * The prefix for Bitcoin script hashes.
       */
      scriptHash: 0x05,
    },
    testnet: {
      /**
       * The prefix for Bitcoin public key hashes.
       */
      pubKeyHash: 0x6f,
      /**
       * The prefix for Bitcoin script hashes.
       */
      scriptHash: 0x05,
    },
  },
  dogecoin: {
    mainnet: {
      /**
       * The prefix for Bitcoin public key hashes.
       */
      pubKeyHash: 0x1e,
      /**
       * The prefix for Bitcoin script hashes.
       */
      scriptHash: 0x16,
    },
    testnet: {
      /**
       * The prefix for Bitcoin public key hashes.
       */
      pubKeyHash: 0x71,
      /**
       * The prefix for Bitcoin script hashes.
       */
      scriptHash: 0xc4,
    },
  },
  zcash: {
    mainnet: {
      // Transparent addresses (t-address)
      pubKeyHash: 0x1cb8, // t1
      scriptHash: 0x1cbd, // t3
      // For shielded addresses (zs) and unified (u1), we can use HRP instead
      saplingHrp: 'zs',
      unifiedHrp: 'u',
      sproutHrp: 'zc',
    },
    testnet: {
      pubKeyHash: 0x1d25, // t-address testnet equivalent
      scriptHash: 0x1cba, // t3 testnet equivalent
      saplingHrp: 'ztestsapling',
      unifiedHrp: 'utest',
      sproutHrp: 'zctest',
    },
  },
  litecoin: {
    mainnet: {
      /**
       * Base58 P2PKH (L...):
       * version byte 0x30 (48)
       */
      pubKeyHash: 0x30,
      /**
       * Base58 P2SH (M...) — preferred:
       * version byte 0x32 (50)
       */
      scriptHash: 0x32,
      /**
       * Base58 P2SH (3...) — legacy but still valid:
       * version byte 0x05 (5)
       */
      scriptHashLegacy: 0x05,
      /**
       * SegWit HRP: ltc (ltc1... for v0 bech32 and v1 bech32m)
       */
      hrp: 'ltc',
    },
    testnet: {
      /**
       * Base58 P2PKH (m... / n...):
       * version byte 0x6f (111)
       */
      pubKeyHash: 0x6f,
      /**
       * Base58 P2SH (Q...) — preferred:
       * version byte 0x3a (58)
       */
      scriptHash: 0x3a,
      /**
       * Base58 P2SH (2...) — legacy:
       * version byte 0xc4 (196)
       */
      scriptHashLegacy: 0xc4,
      /**
       * SegWit HRP: tltc (tltc1... for v0 bech32 and v1 bech32m)
       */
      hrp: 'tltc',
    },
  },
};
